/**
 * The buyer's target-location input (plan 454, M2). The FE classifies what the
 * buyer SUPPLIED and never decides whether it resolves: the server is the one
 * authority (`target_unresolvable` 422, decision 566) and Google resolves
 * server-side (decision 567). So there are only three kinds:
 *
 *   zip   - a typed 5-digit ZIP.
 *   place - a Google suggestion the buyer picked; carries its `place_id` so the
 *           server resolves the SAME place instead of re-searching the label.
 *   text  - anything else, committed as typed with no `place_id`.
 */
export type TargetLocationKind = "zip" | "place" | "text";

export interface TargetLocation {
  label: string;
  kind: TargetLocationKind;
  /** Present only for `kind: "place"`. */
  place_id?: string;
}

const ZIP_RE = /^\d{5}(?:-\d{4})?$/;
const ADDRESS_RE =
  /^\d+\s+.+\b(?:ave|avenue|blvd|boulevard|cir|circle|ct|court|dr|drive|hwy|highway|ln|lane|loop|pkwy|parkway|pl|place|rd|road|st|street|ter|terrace|trl|trail|way)\b\.?/i;

/**
 * Classifies a committed value. `picked` is the suggestion the buyer chose, if
 * any; it only counts while the value still equals its label, so a place_id can
 * never outlive the text it was picked for. A specific street address is not a
 * target location (it is redirected to saved properties) and returns null.
 */
export function resolveTargetLocation(
  value: string | null | undefined,
  picked?: TargetLocation | null,
): TargetLocation | null {
  const input = value?.trim().replace(/\s+/g, " ");
  if (!input || looksLikeSpecificAddress(input)) {
    return null;
  }

  if (ZIP_RE.test(input)) {
    const zip = input.slice(0, 5);
    return { label: zip, kind: "zip" };
  }

  if (picked?.place_id && picked.label.trim() === input) {
    return { label: input, kind: "place", place_id: picked.place_id };
  }

  return { label: input, kind: "text" };
}

/**
 * Client-side input errors only: empty, or a street address. Whether a
 * city/ZIP actually resolves is the server's call and arrives as a
 * `target_unresolvable` 422 (see `lib/target-unresolvable.ts`).
 */
export function getTargetLocationError(value: string | null | undefined) {
  const input = value?.trim();
  if (!input) {
    return "Target area is required";
  }
  if (looksLikeSpecificAddress(input)) {
    return "Use a broader area here, or add the address as a property.";
  }
  return null;
}

export function looksLikeSpecificAddress(value: string | null | undefined) {
  const input = value?.trim();
  return input ? ADDRESS_RE.test(input) : false;
}

/**
 * A picker row: the `TargetLocation` that is committed when it is chosen, plus
 * Google's own `mainText` for the prediction. The picker's prefix rule matches
 * the typed text against `mainText`, not against a split of the label: for a
 * postal code Google's label is "Seattle, WA 98107" but its main text is
 * "98107". Internal to the picker; `mainText` never rides a save.
 */
export interface TargetLocationSuggestion extends TargetLocation {
  mainText: string;
}
